import React, {useEffect, useRef, useState} from "react";
import {ReactComponent as ChevronDownIcon} from "@assets/icons/chevron-down.svg";
import {ReactComponent as CheckmarkIcon} from "@assets/icons/checkmark.svg";
import {classNames} from "@utils/class-names";
import {RichTextTool, TextOptions} from "@common/components/forms/rich-text-form-field/tools/tools-types.enum";

const textOptions = [
    {value: TextOptions.HEADING1, label: "Nagłówek", class: "font-semibold text-[22px] leading-[26px]"},
    {value: TextOptions.HEADING2, label: "Podtytuł", class: "font-semibold text-lg leading-[22px]"},
    {value: TextOptions.PARAGRAPH, label: "Treść"},
    {value: TextOptions.BULLET, label: "• Lista punktowana"},
    {value: TextOptions.NUMBERED, label: "1. Lista numerowana"},
    {value: TextOptions.DASH, label: "- Lista kreskowana"},
    {value: TextOptions.REMOVE_LIST, label: "Usuń listę"},
];

interface TextStyleDropdownProps {
    currentBlockType: string;
    onChangeBlockType: (type: string) => void;
    hiddenOptions?: RichTextTool[];
}

const TextStyleDropdown = (props: TextStyleDropdownProps) => {
    const {
        currentBlockType,
        onChangeBlockType,
        hiddenOptions = [],
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState(currentBlockType);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (currentBlockType && currentBlockType !== selected) {
            setSelected(currentBlockType);
        }
    }, [currentBlockType]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const visibleOptions = textOptions.filter(o => !hiddenOptions.includes(o.value as RichTextTool));

    const handleSelect = (value: string) => {
        setSelected(value);
        onChangeBlockType(value);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(prev => !prev)}
                className="flex items-center justify-between cursor-pointer w-fit h-10 pl-5 pr-5 bg-transparent font-semibold text-base border-none outline-none hover:bg-on-surface/5 !rounded-[64px]"
            >
                <span className="block mr-4">
                    {visibleOptions.find(o => o.value === selected)?.label ?? "Treść"}
                </span>
                <ChevronDownIcon className={`transition-transform ${isOpen ? "rotate-180" : ""}`}/>
            </button>

            {isOpen && (
                <div
                    className="absolute top-full left-0 mt-2 pt-2 bg-white rounded-2xl shadow-lg border border-gray-200 z-10">
                    {visibleOptions.map(option => (
                        <button
                            key={option.value}
                            onClick={() => handleSelect(option.value)}
                            className={classNames(
                                "w-full min-w-full text-left h-[40px] px-[56px] whitespace-nowrap relative",
                                selected === option.value ? "bg-success/10" : "hover:bg-on-surface/5",
                                option.class
                            )}
                        >
                            {option.label}
                            {selected === option.value && (
                                <CheckmarkIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-success"/>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TextStyleDropdown;
